import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Loader2, CreditCard, Clock, FileCheck, AlertCircle, ShieldCheck } from 'lucide-react';
import { xhrFetchAuth, xhrFetchAuthPost } from '@/lib/xhrFetch';
import { API_URL } from '@/config/api';

const AdminActivationTrackerPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState([]);
  const [waived, setWaived] = useState([]);
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  const [waiveJobId, setWaiveJobId] = useState(null);
  const [waiveNote, setWaiveNote] = useState('');
  const [waiving, setWaiving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await xhrFetchAuth(`${API_URL}/api/admin/activation-tracker`);
      if (res.ok && res.json) {
        setPending(res.json.pending || []);
        setWaived(res.json.waived || []);
      } else {
        setError(res.json?.detail || 'Failed to load tracker data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleWaive = (jobId) => {
    setWaiveJobId(jobId);
    setWaiveNote('');
    setShowWaiveModal(true);
  };

  const confirmWaive = async () => {
    if (!waiveJobId) return;
    setWaiving(true);
    try {
      const res = await xhrFetchAuthPost(`${API_URL}/api/admin/activation-tracker/waive`, {
        job_id: waiveJobId,
        waiver_note: waiveNote
      });
      if (res.ok) {
        await fetchData();
        setShowWaiveModal(false);
      } else {
        setError(res.json?.detail || 'Failed to waive fee');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setWaiving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Activation Tracker</h1>
            <p className="text-slate-400 mt-1">Manuscript activation fees and waivers</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-slate-400">
            Back to Dashboard
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{error}</span>
          </div>
        )}

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 mb-6">
            <TabsTrigger value="pending" className="data-[state=active]:bg-slate-700" data-testid="pending-tab">
              <Clock className="w-4 h-4 mr-2" />
              Pending Activation ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="waived" className="data-[state=active]:bg-slate-700" data-testid="waived-tab">
              <FileCheck className="w-4 h-4 mr-2" />
              Waived ({waived.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Pending Activation</CardTitle>
                <CardDescription className="text-slate-400">
                  Completed manuscripts awaiting activation payment
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pending.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No pending activations</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pending-table">
                      <thead>
                        <tr className="border-b border-slate-700 text-slate-400">
                          <th className="text-left py-3 px-4">Author</th>
                          <th className="text-left py-3 px-4">Title</th>
                          <th className="text-left py-3 px-4">Completed</th>
                          <th className="text-left py-3 px-4">Status</th>
                          <th className="text-right py-3 px-4">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((job) => (
                          <tr key={job.job_id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`pending-row-${job.job_id}`}>
                            <td className="py-3 px-4 text-white">{job.author_name}</td>
                            <td className="py-3 px-4 text-slate-300">{job.manuscript_title}</td>
                            <td className="py-3 px-4 text-slate-400">{formatDate(job.completed_at)}</td>
                            <td className="py-3 px-4">
                              <span className="text-amber-400 bg-amber-400/10 px-2 py-1 rounded text-xs">
                                Awaiting Payment
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                                onClick={() => handleWaive(job.job_id)}
                                data-testid={`waive-btn-${job.job_id}`}
                              >
                                <ShieldCheck className="w-4 h-4 mr-1" />
                                Waive Fee
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="waived">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Waived Activations</CardTitle>
                <CardDescription className="text-slate-400">
                  Activation fees waived by admin for accounting record
                </CardDescription>
              </CardHeader>
              <CardContent>
                {waived.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No waived activations</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="waived-table">
                      <thead>
                        <tr className="border-b border-slate-700 text-slate-400">
                          <th className="text-left py-3 px-4">Author</th>
                          <th className="text-left py-3 px-4">Title</th>
                          <th className="text-left py-3 px-4">Waived Date</th>
                          <th className="text-left py-3 px-4">Waiver Note</th>
                          <th className="text-left py-3 px-4">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waived.map((job) => (
                          <tr key={job.job_id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`waived-row-${job.job_id}`}>
                            <td className="py-3 px-4 text-white">{job.author_name}</td>
                            <td className="py-3 px-4 text-slate-300">{job.manuscript_title}</td>
                            <td className="py-3 px-4 text-slate-400">{formatDate(job.waived_at)}</td>
                            <td className="py-3 px-4 text-slate-400">{job.waiver_note || '—'}</td>
                            <td className="py-3 px-4 text-green-400">$0.00</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Waive Fee Modal */}
      <Dialog open={showWaiveModal} onOpenChange={(open) => { if (!open) setShowWaiveModal(false); }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm" data-testid="waive-fee-modal">
          <DialogHeader>
            <DialogTitle className="text-white">Waive Activation Fee</DialogTitle>
            <DialogDescription className="text-slate-400">
              This waiver does not activate the manuscript. It allows the author to activate without a charge.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm text-slate-300 mb-2">Reason (optional)</label>
            <Input
              value={waiveNote}
              onChange={(e) => setWaiveNote(e.target.value)}
              placeholder="e.g. Beta tester, promotional, etc."
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="waive-note-input"
            />
          </div>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => setShowWaiveModal(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={confirmWaive}
              disabled={waiving}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="confirm-waive-btn"
            >
              {waiving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Confirm Waiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminActivationTrackerPage;
